import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import {
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Box,
  Typography,
  Badge,
} from "@mui/material";
import {
  FaUser,
  FaSignOutAlt,
  FaCog,
  FaRegNewspaper,
  FaGlobe,
} from "react-icons/fa";
import {
  SUPPORTED_LANGUAGES,
  SUPPORTED_LANGUAGE_CODES,
  SupportedLanguageCode,
} from "../i18n";
import { getCurrentUser, setCurrentUser, isAdmin } from "../utils/auth";
import api from "../services/api";

interface UserAvatarMenuProps {
  user: {
    username: string;
    email?: string;
    role: string;
    first_name?: string;
    last_name?: string;
  };
  onLogout: () => void;
  newFeedbackCount: number;
}

function getInitials(user: UserAvatarMenuProps["user"]): string {
  if (user.first_name && user.last_name) {
    return (user.first_name[0] + user.last_name[0]).toUpperCase();
  }
  if (!user.username) return "?";
  const parts = user.username.split(".");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return user.username[0].toUpperCase();
}

const UserAvatarMenu: React.FC<UserAvatarMenuProps> = ({
  user,
  onLogout,
  newFeedbackCount,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("common");
  const open = Boolean(anchorEl);

  const handleClose = () => setAnchorEl(null);

  const persistLanguageMutation = useMutation(
    (code: string) => api.user.updateProfile({ preferred_language: code }),
    {
      onError: (err) => {
        console.warn("Failed to persist language preference:", err);
      },
    },
  );

  const handleLanguageChange = (code: string) => {
    handleClose();
    if (code === i18n.language) return;
    if (!SUPPORTED_LANGUAGE_CODES.includes(code as SupportedLanguageCode)) {
      console.error(`Unsupported language code: ${code}`);
      return;
    }

    i18n.changeLanguage(code);
    localStorage.setItem("i18nextLng", code);

    const currentUser = getCurrentUser();
    if (currentUser) {
      setCurrentUser({ ...currentUser, preferred_language: code });
    }

    persistLanguageMutation.mutate(code);
  };

  const handleNavigate = (path: string) => {
    handleClose();
    navigate(path);
  };

  return (
    <>
      <IconButton
        onClick={(e) => setAnchorEl(e.currentTarget)}
        size="small"
        data-testid="user-menu"
        aria-label={t("navigation.profile")}
        aria-controls={open ? "user-avatar-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      >
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: "#1976D2",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {getInitials(user)}
        </Avatar>
      </IconButton>
      <Menu
        id="user-avatar-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        slotProps={{
          paper: {
            sx: { width: 240, mt: 1 },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {user.first_name && user.last_name
              ? `${user.first_name} ${user.last_name}`
              : user.username}
          </Typography>
          {user.email && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {user.email}
            </Typography>
          )}
        </Box>
        <Divider />
        <MenuItem onClick={() => handleNavigate("/profile")}>
          <FaUser style={{ marginRight: 12 }} />
          {t("navigation.profile")}
        </MenuItem>
        {isAdmin() && (
          <MenuItem onClick={() => handleNavigate("/admin")}>
            <Badge
              badgeContent={newFeedbackCount}
              color="error"
              sx={{ mr: 1.5 }}
            >
              <FaCog />
            </Badge>
            {t("navigation.admin")}
          </MenuItem>
        )}
        <MenuItem onClick={() => handleNavigate("/release-notes")}>
          <FaRegNewspaper style={{ marginRight: 12 }} />
          {t("navigation.releaseNotes", "Release Notes")}
        </MenuItem>
        <Divider />
        <Box sx={{ px: 2, py: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            <FaGlobe style={{ marginRight: 6, verticalAlign: "middle" }} />
            {t("actions.changeLanguage", { defaultValue: "Language" })}
          </Typography>
        </Box>
        {SUPPORTED_LANGUAGES.map((lang) => (
          <MenuItem
            key={lang.code}
            selected={lang.code === i18n.language}
            onClick={() => handleLanguageChange(lang.code)}
            sx={{ fontSize: 14, justifyContent: "space-between" }}
          >
            <span>{lang.nativeName}</span>
            <Typography variant="caption" color="text.secondary">
              {lang.code.toUpperCase()}
            </Typography>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={onLogout} data-testid="logout">
          <FaSignOutAlt style={{ marginRight: 12 }} />
          {t("navigation.logout")}
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserAvatarMenu;
